/**
 * Payment-method guard layer (Sweden phase).
 *
 * This module is the single seam that decides which currencies and payment
 * methods are allowed. The marketplace runs a "Sweden only" (SEK) phase and
 * checkout offers card, Apple Pay and Google Pay — nothing else (Christofer,
 * 2026-09-24, P25009-461). Klarna was dropped permanently after incident
 * WO-20006; MobilePay does not support SEK.
 *
 * Future global expansion (DK/FI MobilePay, EUR iDEAL, …) is a small change
 * here — add the currency to SUPPORTED_CURRENCIES and the method to
 * getAvailablePaymentMethods — plus the server's CHECKOUT_PAYMENT_METHOD_TYPES.
 *
 * A CommonJS twin lives at server/api-util/paymentMethods.js for the Node server
 * (PaymentIntent creation, validate endpoint, capture cron). Keep the two in
 * sync: this file is ESM/bundled, the server is CommonJS. The SERVER decides
 * what Stripe actually shows: it creates every PaymentIntent card-only.
 */

// Currencies the marketplace accepts in the current phase. Sweden only for now.
export const SUPPORTED_CURRENCIES = ['SEK'];

/**
 * Stripe PaymentIntent statuses that mean the buyer has finished their part and
 * the booking may be confirmed. Our transaction PaymentIntents use manual
 * capture, so an authorised payment sits at 'requires_capture' (cards, Apple
 * Pay, Google Pay) until the capture cron captures it; immediate-capture flows
 * reach 'succeeded'. Anything else ('processing', 'requires_action',
 * 'requires_payment_method', …) is NOT yet confirmable.
 */
const CONFIRMABLE_STATUSES = ['succeeded', 'requires_capture'];

/** True when `currency` is accepted in the current phase (case-insensitive). */
export const isCurrencySupported = currency =>
  !!currency && SUPPORTED_CURRENCIES.includes(String(currency).toUpperCase());

/**
 * Apple Pay is only offered where the browser exposes a usable ApplePaySession
 * (Safari on iOS/macOS, Chrome on iOS). It is hidden everywhere else. This is a
 * client-only check — there is no window/ApplePaySession on the server (SSR).
 */
export const isApplePayAvailable = () =>
  typeof window !== 'undefined' &&
  !!window.ApplePaySession &&
  typeof window.ApplePaySession.canMakePayments === 'function' &&
  window.ApplePaySession.canMakePayments();

/**
 * MobilePay is intentionally disabled in the Sweden phase: it does not support
 * SEK or Swedish Stripe accounts. Always false for now; enable per market when
 * a DK/FI Stripe account exists.
 */
// eslint-disable-next-line no-unused-vars
export const isMobilePayAvailable = (currency, country) => false;

/**
 * Whether a transaction may be confirmed given a Stripe PaymentIntent status.
 * Used to gate the confirm-payment transition so a payment is never confirmed
 * on the browser redirect alone while still 'processing'.
 */
export const canConfirmTransaction = status => CONFIRMABLE_STATUSES.includes(status);

/**
 * The payment methods offered for a currency/country: card, with Apple Pay and
 * Google Pay as card wallets. Stripe renders a wallet only where the device
 * supports it (isApplePayAvailable for Apple Pay) and the domain is registered.
 * Adding MobilePay for DK/FI later is a single entry here.
 */
export const getAvailablePaymentMethods = (currency, country) => {
  if (!isCurrencySupported(currency)) {
    return [];
  }
  const methods = ['card', 'apple_pay', 'google_pay'];
  if (isMobilePayAvailable(currency, country)) {
    methods.push('mobilepay');
  }
  return methods;
};
